/** The PUT's 400 (`{detail, errors: {"<path>": "message"}}`) in the record editor's terms:
 *  each message on the change it concerns, so it shows on its field and its ledger row.
 *
 *  - A settings key, a field list or the waivers (`pre_check_settings.<key>`,
 *    `…lead_booker_fields`, `…party_member_fields`, `…waivers`) are draft paths as they
 *    stand: on that change when the save carried it.
 *  - A custom field's problem (`definitions.<id>`, `….name`, `….type`) and the list's own
 *    (`definitions`) go on the `definitions` change, named for the ledger ("“Shoe size”:
 *    Can't change the type …") and keyed by the field's ID (`contact_key`, the core's item
 *    key of a list-valued field) — the Custom fields table puts it on that row, never on
 *    another field that happens to share the label.
 *  - `reason` is the description's; the rest (`pre_check_settings`, `fields`, `version`, a
 *    path the save did not change, a bare `detail`) are form-level — named, so the ledger's
 *    summary says which field. */
import type { OperatorPreCheckErrors } from '@/api/operatorPrecheck';
import type { CustomFieldDefinition } from '@/features/precheck/model';
import { changeKey, type DraftWireChange, type RecordKey } from '@/features/records/draft/changeSet';
import type { ValidationFailure } from '@/features/records/editor/errors';
import type { RecordFieldError } from '@/features/records/editor/types';

import { OPERATOR_PRECHECK_FIELD_BY_KEY } from './catalog';
import { DEFINITIONS_PATH } from './model';

/** "“Shoe size”: " — how a custom field's message is named in the ledger. */
export const definitionPrefix = (name: string) => `“${name}”: `;

/** A custom field's message without its name — under its own row the row names it. */
export const withoutDefinitionPrefix = (message: string) => message.replace(/^“[^”]*”: /, '');

export function operatorPreCheckFailure(
  body: OperatorPreCheckErrors,
  ctx: {
    rec: RecordKey;
    sent: readonly DraftWireChange[];
    /** The custom fields as the save sent them, then as stored — their labels. */
    definitions: readonly CustomFieldDefinition[];
  },
): ValidationFailure {
  const errors: Record<string, RecordFieldError[]> = {};
  const add = (key: string, message: string, item?: string) =>
    (errors[key] ??= []).push({ code: 'invalid', message, ...(item ? { contact_key: item } : {}) });
  const sent = new Set(ctx.sent.map((c) => changeKey(`${c.entity}:${c.id}`, c.field ?? c.action ?? '')));
  const onChange = (path: string, message: string, formLabel?: string, item?: string) => {
    const key = changeKey(ctx.rec, path);
    if (sent.has(key)) add(key, message, item);
    else add(path, formLabel ? `${formLabel}: ${message}` : message);
  };
  const nameOf = (id: string) => ctx.definitions.find((d) => d.id === id)?.name || id;

  for (const [path, message] of Object.entries(body.errors ?? {})) {
    if (path === 'reason') {
      add('description', message);
    } else if (path.startsWith(`${DEFINITIONS_PATH}.`)) {
      const rest = path.slice(DEFINITIONS_PATH.length + 1);
      const id = rest.replace(/\.(name|type)$/, '');
      onChange(DEFINITIONS_PATH, `${definitionPrefix(nameOf(id))}${message}`, 'Custom fields', id);
    } else {
      const field = OPERATOR_PRECHECK_FIELD_BY_KEY.get(path);
      if (field) onChange(path, message, field.label);
      else add(path, message);
    }
  }
  if (Object.keys(errors).length === 0) add('detail', body.detail || 'The server refused the changes.');
  return { code: 'invalid', message: body.detail || 'The server refused the changes.', errors };
}
